/**
 * ShiftHourChart — 24 bars, each coloured by the shift that owns that hour.
 *
 * One chart rather than three sparklines. The question it answers is "when
 * INSIDE a shift does the load or the delay fall", and the answer is almost
 * always the first or last hour — which is the shift-change panel's question
 * seen from the other side. Three separate charts would put each shift on its
 * own scale and hide exactly that.
 *
 * Plain divs, no chart library. `getMainDashHoursChartV2` + `ShiftLegend`
 * already produce this idiom for the dashboard, so it reads as the same product,
 * and a 24-bar bar chart does not justify a dependency.
 *
 * Bars are sized with `flex` and percentage heights, not by measuring the
 * container: layout measurement inside this app's browser harness is unreliable
 * (a viewport that reports 0x0, ResizeObserver callbacks that never fire), and a
 * chart that needs JS to know its own width is a chart that renders blank on
 * first paint.
 */

import {
    DASH,
    formatCount,
    formatHourLabel,
    formatHourShort,
    formatMinutes,
    shiftColor,
} from '../../../../utils/shiftFormat.js';

/* Bars are drawn against events by default; response time is the other question
   this data answers, so both are offered rather than picking one. */
const MODES = [
    { id: 'events', label: 'Events', pick: (h) => h.total_events, fmt: formatCount, unit: '' },
    { id: 'response', label: 'Avg Response', pick: (h) => h.avg_response_time, fmt: formatMinutes, unit: ' min' },
];

const ShiftHourChart = ({ hours = [], mode = 'events', onModeChange }) => {
    const active = MODES.find((m) => m.id === mode) || MODES[0];

    /* Peak over the CHOSEN measure. A shared scale across both measures would
       flatten one of them into invisibility — events run to the hundreds while
       response minutes run to single digits. */
    const values = (hours || []).map((h) => Number(active.pick(h)) || 0);
    const peak = values.length ? Math.max(...values) : 0;

    if (!hours.length) {
        return (
            <div className="sr-card">
                <div className="sr-card__head">
                    <h3 className="sr-card__title">By hour of day</h3>
                </div>
                <p className="sr-empty">No hourly data for this period.</p>
            </div>
        );
    }

    return (
        <div className="sr-card">
            <div className="sr-card__head">
                <div>
                    <h3 className="sr-card__title">By hour of day</h3>
                    <p className="sr-card__sub">
                        Where inside a shift the load and the delays actually fall.
                    </p>
                </div>
                {onModeChange ? (
                    <div className="pp-subtab-toggle sr-chart-toggle">
                        {MODES.map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                className={`pp-subtab-btn${m.id === active.id ? ' is-active' : ''}`}
                                onClick={() => onModeChange(m.id)}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>

            <div className="sr-chart">
                {hours.map((h) => {
                    const raw = active.pick(h);
                    const val = Number(raw) || 0;
                    /* A zero-value hour still gets a 2px stub so the axis reads
                       as 24 hours rather than as gaps. An hour with NO measurable
                       response (raw null) is left bare — the difference between
                       "nothing happened" and "nothing measurable" is worth
                       seeing. */
                    const pct = peak > 0 ? (val / peak) * 100 : 0;
                    const bare = raw === null || raw === undefined;
                    return (
                        <div className="sr-chart__col" key={h.local_hour}>
                            <div className="sr-chart__track">
                                <div
                                    className={`sr-chart__bar${bare ? ' sr-chart__bar--bare' : ''}`}
                                    style={{
                                        height: bare ? '0' : `max(2px, ${pct}%)`,
                                        background: shiftColor(h.shift_name),
                                    }}
                                    title={`${formatHourLabel(h.local_hour)} · ${h.shift_name || 'Unassigned'}`
                                        + `\n${formatCount(h.total_events)} events`
                                        + `\nAvg response ${h.avg_response_time === null || h.avg_response_time === undefined
                                            ? DASH : `${formatMinutes(h.avg_response_time)} min`}`}
                                />
                            </div>
                            {/* Every third hour, so the axis stays legible at
                                phone width without needing a media query. */}
                            <span className="sr-chart__tick">
                                {h.local_hour % 3 === 0 ? formatHourShort(h.local_hour) : ''}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="sr-chart__peak">
                {peak > 0
                    ? `Tallest bar = ${active.fmt(peak)}${active.unit}`
                    : 'No events in this period.'}
            </div>
        </div>
    );
};

export default ShiftHourChart;
